import type { FC, ReactNode } from 'react';

import React, { lazy, Suspense } from 'react';

import { TronProvider } from './sdk.client';

interface BlockchainProviderProps {
  children: ReactNode;
}

// Every enabled family is mounted at once (multichain spec §4.6): a build with TRON and Base
// carries both wallet contexts side by side, and the chain of a deal decides which one a screen
// talks to — no installation-wide chain any more.
//
// Families this build does not enable come in through a dynamic import behind a literal build
// constant, and that is not cosmetic. A static import runs the module body at load time, and
// evm/config.ts builds its wagmi config at module scope — a plain `import` therefore initialised
// the whole connector set in a TRON-only build, where it has nothing to connect to, and logged an
// EIP1193 timeout on every page. Rollup cannot drop that: a top-level call is a side effect.
// The `__EVM_ENABLED__ ? … : null` guard is not redundant either — a bare `lazy(() => import(…))`
// stays reachable, so the chunk is still emitted and the image carries a bundle nothing can load.
//
// TON is not part of the multichain model (spec §2) and is referenced from nowhere: its scaffold
// under ./ton stays in the tree, is never imported, and never reaches a bundle.
//
// The server mounts no wallet context at all (`import.meta.env.SSR` is a literal too): a wallet
// lives in the visitor's browser, and its SDK reaches for `window` the moment a provider is built.
// What the server does keep is the tree's shape — the `Suspense` an EVM build wraps the app in —
// because the browser takes the server's markup over boundary by boundary.
const EvmProvider =
  __EVM_ENABLED__ && !import.meta.env.SSR
    ? lazy(() => import('./evm/EvmProvider').then((m) => ({ default: m.EvmProvider })))
    : null;

// TRON stays a static import: it is in every configuration we ship, and it is the chain whose
// wallet the app needs on first paint. Putting it behind `lazy` would place the entire app under
// Suspense on the primary chain for no gain. Make it lazy the day a TRON-less build is shipped.

export const BlockchainProvider: FC<BlockchainProviderProps> = ({ children }) => {
  let tree = <>{children}</>;

  if (__EVM_ENABLED__) {
    tree = <Suspense fallback={null}>{EvmProvider ? <EvmProvider>{tree}</EvmProvider> : tree}</Suspense>;
  }

  if (__TRON_ENABLED__ && !import.meta.env.SSR) {
    tree = <TronProvider>{tree}</TronProvider>;
  }

  return tree;
};
